import { Search, X } from 'lucide-react';
import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from 'react';

import { cn } from './cn';

type NativeInput = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'>;

/**
 * Con `exactOptionalPropertyTypes`, una prop opcional que se REENVIA debe
 * admitir `undefined` de forma explicita: "ausente" y "presente con valor
 * undefined" son tipos distintos, y quien envuelve este componente propaga lo
 * segundo.
 */
/**
 * Densidad del campo.
 *
 * `sm` es la del sistema y sigue siendo la predeterminada: las pantallas de
 * este producto son tablas densas, y un campo alto ahi roba filas. `lg` existe
 * para las pantallas de UNA sola tarea -el acceso, el cambio de contrasena-,
 * donde no hay densidad que conservar y un campo de 32 px con texto de 13 se
 * lee mal y se acierta peor con el dedo en un terminal de almacen.
 */
export type InputSize = 'sm' | 'lg';

const CAJA: Readonly<Record<InputSize, string>> = {
  // 36 px: la misma altura que `Button` en `md` y que `Select`. Conviven en la
  // barra de filtros de casi todas las pantallas y un pixel de diferencia se
  // ve como un escalon.
  sm: 'h-9 gap-2 px-3',
  lg: 'h-11 gap-2.5 px-3.5',
};

const TEXTO: Readonly<Record<InputSize, string>> = {
  sm: 'text-sm',
  lg: 'text-base',
};

const ETIQUETA: Readonly<Record<InputSize, string>> = {
  sm: 'text-xs',
  lg: 'text-sm',
};

export interface InputProps extends NativeInput {
  readonly label?: string | undefined;
  readonly inputSize?: InputSize | undefined;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly leading?: ReactNode | undefined;
  readonly trailing?: ReactNode | undefined;
  readonly className?: string | undefined;
  readonly ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * Campo de texto.
 *
 * El error se muestra bajo el campo y ademas se enlaza con `aria-describedby`
 * y `aria-invalid`: un borde rojo solo comunica el problema a quien ve la
 * pantalla, y el mensaje tiene que llegar tambien al lector de pantalla.
 *
 * El estado de error NO sustituye al texto de ayuda: se anaden, porque el
 * usuario suele necesitar el formato esperado justo cuando se equivoco.
 */
function FieldLabel({
  htmlFor,
  text,
  required,
  size,
}: {
  htmlFor: string;
  text: string;
  required: boolean;
  size: InputSize;
}) {
  return (
    <label htmlFor={htmlFor} className={cn('font-medium text-fg-body', ETIQUETA[size])}>
      {text}
      {required && (
        <span className="ml-0.5 text-st-out" aria-hidden="true">
          *
        </span>
      )}
    </label>
  );
}

function FieldMessages({
  hintId,
  hint,
  errorId,
  error,
}: {
  hintId: string;
  hint: string | undefined;
  errorId: string;
  error: string | undefined;
}) {
  return (
    <>
      {hint !== undefined && (
        <p id={hintId} className="text-2xs text-fg-subtle">
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={errorId} role="alert" className="text-2xs font-medium text-st-out">
          {error}
        </p>
      )}
    </>
  );
}

export function Input({
  label,
  hint,
  error,
  leading,
  trailing,
  className,
  inputSize = 'sm',
  id,
  required,
  ref,
  ...rest
}: InputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const invalid = error !== undefined;

  const describedBy =
    [hint !== undefined ? hintId : null, invalid ? errorId : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      {label !== undefined && (
        <FieldLabel htmlFor={inputId} text={label} required={required === true} size={inputSize} />
      )}

      <div
        className={cn(
          'flex items-center rounded-md border bg-panel',
          CAJA[inputSize],
          'transition-[border-color,box-shadow] duration-(--t-tap)',
          'focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/18',
          invalid ? 'border-st-out' : 'border-line-strong hover:border-n300',
        )}
      >
        {leading !== undefined && (
          <span className="shrink-0 text-fg-subtle" aria-hidden="true">
            {leading}
          </span>
        )}

        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-full w-full min-w-0 bg-transparent text-fg outline-none placeholder:text-fg-subtle',
            TEXTO[inputSize],
          )}
          {...rest}
        />

        {trailing !== undefined && <span className="shrink-0">{trailing}</span>}
      </div>

      <FieldMessages hintId={hintId} hint={hint} errorId={errorId} error={error} />
    </div>
  );
}

/**
 * Campo de busqueda.
 *
 * Lleva boton de limpiar porque en una tabla filtrada volver al listado
 * completo es una accion frecuente, y borrar a mano lo escrito es la forma
 * mas lenta de hacerlo.
 */
export function SearchInput({
  value,
  onValueChange,
  placeholder = 'Buscar',
  className,
  ...rest
}: Omit<InputProps, 'leading' | 'trailing' | 'onChange' | 'value'> & {
  value: string;
  onValueChange: (value: string) => void;
}) {
  return (
    <Input
      {...rest}
      value={value}
      type="search"
      placeholder={placeholder}
      className={className}
      onChange={(event) => {
        onValueChange(event.target.value);
      }}
      leading={<Search className="size-3.5" />}
      trailing={
        value.length > 0 ? (
          <button
            type="button"
            aria-label="Limpiar busqueda"
            onClick={() => {
              onValueChange('');
            }}
            className="flex size-4 items-center justify-center rounded-xs text-fg-subtle transition-colors duration-(--t-tap) hover:bg-sunken hover:text-fg"
          >
            <X className="size-3" aria-hidden="true" />
          </button>
        ) : undefined
      }
    />
  );
}
